import React, { useCallback, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import Link from '@atlaskit/link/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack } from '@atlaskit/primitives/stack';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Text } from '@atlaskit/primitives/text';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { ConfigPanelFeedbackCollector } from '@atlassian/company-hub-common/config-panel/feedback-collector';
import { PremiumGateForExtensionConfigPanel } from '@atlassian/company-hub-common/config-panel/premium-gate-state';
import { ConfigPanelGrouping } from '@atlassian/company-hub-common/config-panel/section-grouping';
import { SPOTLIGHT_EXTENSION_TYPE } from '@atlassian/company-hub-common/constants';
import { useProductDependencies } from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { ExtensionsConfigWrapper } from '@atlassian/company-hub-common/extensions-config-wrapper';
import { LayoutOptions } from '@atlassian/company-hub-common/layout-options';
import {
	ManageTranslations,
	ManageTranslationsOpenProvider,
	useManageTranslationsOpen,
} from '@atlassian/company-hub-common/manage-translations';
import { NoExtensionData } from '@atlassian/company-hub-common/no-extension-data';
import type { PreviewSizeProps } from '@atlassian/company-hub-common/preview-size';
import { type CommonLayout, FeedbackType } from '@atlassian/company-hub-common/types';

import { layoutOptions, SPOTLIGHT_EXTENSION_KEY } from '../../../../constants';
import { useSpotlightParametersAndActions } from '../../../../controllers/use-spotlight-parameters-and-actions';
import type { SpotlightParameters } from '../../../../types';
import { Spotlight } from '../../spotlight-extension/spotlight';
import { i18n } from './messages';
import { GeneralOptions } from './styles-sections/general-options';
import { TextAndImageSection } from './text-and-image-section';
import { i18n as textAndImageMessages } from './text-and-image-section/messages';

const SOURCE = 'spotlightExtensionConfig';

const SUPPORT_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/highlight-important-messages-with-a-visual-spotlight/';

const stickyFooterStyles = xcss({
	paddingInline: 'space.050',
	paddingBlock: 'space.200',
	display: 'flex',
	flexDirection: 'column',
	position: 'sticky',
	bottom: 'space.0',
	backgroundColor: 'elevation.surface',
});

const SpotlightFooter = () => (
	<Box xcss={stickyFooterStyles}>
		<ConfigPanelFeedbackCollector source="spotlightExtension" type={FeedbackType.SPOTLIGHT} />
	</Box>
);

export type SpotlightConfigPanelContentProps = {
	closeConfigPanel: () => void;
	contentId: string;
	editorActions: EditorActions;
	extensionKey: string;
	localId: string;
};

export const SpotlightConfigPanelContentInner = ({
	closeConfigPanel,
	localId,
	editorActions,
	extensionKey,
	contentId,
}: SpotlightConfigPanelContentProps): React.JSX.Element => {
	const intl = useIntl();
	const { isOpen: isManageTranslationsOpen } = useManageTranslationsOpen();
	// Retain the selected tab across the manage-translations mount toggle (HUBS-1411).
	const [selectedTab, setSelectedTab] = useState(0);
	const { spotlightParameters, spotlightActions } = useSpotlightParametersAndActions({
		localId,
		editorActions,
	});
	const { showConfigPreambleSupportLink } = useProductDependencies();

	const PreviewContentComponent = useCallback<PreviewSizeProps<CommonLayout>['PreviewComponent']>(
		({ height, layout }) => {
			const extensionNode: ExtensionParams<SpotlightParameters> = {
				extensionKey: SPOTLIGHT_EXTENSION_KEY,
				extensionType: SPOTLIGHT_EXTENSION_TYPE,
				// Casting here to satisfy TypeScript and prevent the linter from thinking
				// useCallback is called conditionally.
				parameters: {
					...spotlightParameters,
					height,
					layout,
				} as SpotlightParameters,
			};

			return <Spotlight isPreviewSize extensionNode={extensionNode} />;
		},
		[spotlightParameters],
	);

	if (!spotlightParameters) {
		return <NoExtensionData source={extensionKey} isConfigPanel />;
	}

	const tabSections = [
		{
			label: intl.formatMessage(i18n.stylesTabLabel),
			value: 'styles',
			tabContent: [
				{
					title: intl.formatMessage(i18n.generalTitle),
					sectionId: 'general',
					defaultIsExpanded: true,
					component: <GeneralOptions parameters={spotlightParameters} actions={spotlightActions} />,
				},
				{
					title: intl.formatMessage(i18n.layoutsTitle),
					sectionId: 'layouts',
					defaultIsExpanded: false,
					component: (
						<LayoutOptions<CommonLayout>
							updateLayout={spotlightActions.updateLayout}
							source={SOURCE}
							layoutOptions={layoutOptions}
							PreviewComponent={PreviewContentComponent}
							currentLayoutSelection={spotlightParameters.layout}
						/>
					),
				},
			],
		},
		{
			label: intl.formatMessage(i18n.contentTabLabel),
			value: 'content',
			tabContent: [
				{
					sectionId: 'main',
					component: (
						<Stack grow="fill" space="space.200">
							<TextAndImageSection
								parameters={spotlightParameters}
								actions={spotlightActions}
								contentId={contentId}
							/>
						</Stack>
					),
				},
			],
		},
	];

	return (
		<>
			{!isManageTranslationsOpen && (
				<ExtensionsConfigWrapper
					closeConfigPanel={closeConfigPanel}
					title={<FormattedMessage {...i18n.title} />}
					footerComponent={SpotlightFooter}
				>
					<Text>
						<FormattedMessage
							{...(showConfigPreambleSupportLink ? i18n.preamble : i18n.preambleWithoutSupportLink)}
							values={{
								supportLink: (
									<Link target="_blank" href={SUPPORT_LINK}>
										<FormattedMessage {...i18n.preambleSupportLinkLabel} />
									</Link>
								),
							}}
						/>
					</Text>
					<PremiumGateForExtensionConfigPanel feature="premiumSpotlightConfig">
						<ConfigPanelGrouping
							source={SOURCE}
							tabSections={tabSections}
							selected={selectedTab}
							onTabClick={(_tab, index) => setSelectedTab(index)}
						/>
					</PremiumGateForExtensionConfigPanel>
				</ExtensionsConfigWrapper>
			)}
			{isManageTranslationsOpen && (
				<ManageTranslations
					parameters={spotlightParameters}
					closeConfigPanel={closeConfigPanel}
					localId={localId}
					extensionType={SPOTLIGHT_EXTENSION_TYPE}
					config={{
						fields: [
							{
								path: 'content.text.title',
								label: intl.formatMessage(textAndImageMessages.titleLabel),
								placeholder: intl.formatMessage(textAndImageMessages.titlePlaceholder),
							},
							{
								path: 'content.text.description',
								label: intl.formatMessage(textAndImageMessages.descriptionLabel),
								placeholder: intl.formatMessage(textAndImageMessages.descriptionPlaceholder),
							},
							{
								path: 'content.button.text',
								label: intl.formatMessage(i18n.buttonFieldLabel),
								placeholder: intl.formatMessage(i18n.buttonFieldPlaceholder),
							},
						],
					}}
				/>
			)}
		</>
	);
};

export const SpotlightConfigPanelContent = (
	props: SpotlightConfigPanelContentProps,
): React.JSX.Element => (
	<ManageTranslationsOpenProvider>
		<SpotlightConfigPanelContentInner {...props} />
	</ManageTranslationsOpenProvider>
);
